import { useMemo, useRef, useState } from "react";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import Banner from "@/components/Banner";
import ErrorBoundary from "@/components/ErrorBoundary";
import IconButton from "@/components/IconButton";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import usePageTitle from "@/hooks/usePageTitle";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { showSnackbar } from "@/lib/snackbarUtils";
import { Navigate, useNavigate, useParams } from "@/router";
import { GET_PERSON_BENEFICIARIES_QUERY } from "../../../person/queries";
import { UPDATE_BENEFICIARIES_MUTATION } from "../../mutations";
import DiscardBeneficiariesChangesDialog from "../_components/DiscardBeneficiariesChangesDialog";
import UpdateBeneficiariesForm, { type UpdateBeneficiariesFormActions } from "../_components/UpdateBeneficiariesForm";
import UpdateBeneficiariesFormSkeleton from "../_components/UpdateBeneficiariesFormSkeleton";
import {
  applicationTypeLabel,
  buildDefaultValues,
  isBeneficiaryApplicationType,
  toMutationInput,
  updateBeneficiaryApplicationTypes,
  type BeneficiariesFormValues
} from "../_components/beneficiaryFormUtils";

export default function UpdateBeneficiariesPage() {
  useRequireCapability({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageBeneficiaries
  });

  const { pgaId = "", applicationType = "" } = useParams(
    "/people/:pgaId/benefits/beneficiaries/update/:applicationType"
  );
  const navigate = useNavigate();
  const [bannerError, setBannerError] = useState("");
  const [formState, setFormState] = useState({ canSave: false, isDirty: false });
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const formActionsRef = useRef<UpdateBeneficiariesFormActions | null>(null);
  const flags = useFlags();
  const contactInfoEnabled = flags["membership-beneficiary-contact-info"] === true;

  const goToBeneficiariesList = () => navigate("/people/:pgaId/benefits/beneficiaries", { params: { pgaId } });

  usePageTitle(
    isBeneficiaryApplicationType(applicationType)
      ? `Update ${applicationTypeLabel(applicationType)} Beneficiaries`
      : "Update Beneficiaries"
  );

  const { data, loading, error } = useQuery(GET_PERSON_BENEFICIARIES_QUERY, {
    variables: { pgaId, includeInactive: true },
    fetchPolicy: "cache-first",
    skip: !pgaId
  });

  const activeRecords = useMemo(() => {
    if (!isBeneficiaryApplicationType(applicationType)) return [];
    return (data?.person?.beneficiaries ?? []).filter(
      (record) => !record.effectiveEndDate && record.applicationType === applicationType
    );
  }, [applicationType, data?.person?.beneficiaries]);

  const allowedApplicationTypes = useMemo(
    () =>
      updateBeneficiaryApplicationTypes(
        data?.person?.latestProgram?.programType?.name,
        (data?.person?.beneficiaries ?? []).filter((record) => !record.effectiveEndDate)
      ),
    [data?.person?.beneficiaries, data?.person?.latestProgram?.programType?.name]
  );

  const defaultValues = useMemo(
    () => buildDefaultValues(activeRecords, applicationType),
    [activeRecords, applicationType]
  );
  const personId = data?.person?.id;
  const formResetKey = `${pgaId}:${applicationType}`;

  const [updateBeneficiaries, { loading: saving }] = useMutation(UPDATE_BENEFICIARIES_MUTATION, {
    refetchQueries: [
      {
        query: GET_PERSON_BENEFICIARIES_QUERY,
        variables: { pgaId, includeInactive: true }
      }
    ],
    awaitRefetchQueries: true
  });

  if (!isBeneficiaryApplicationType(applicationType)) {
    return <Navigate to="/people/:pgaId/benefits/beneficiaries" params={{ pgaId }} replace />;
  }

  const handleCancel = goToBeneficiariesList;

  const handleCancelClick = () => {
    if (formState.isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    handleCancel();
  };

  async function onSubmit(values: BeneficiariesFormValues) {
    if (!personId) return;

    try {
      const { data: mutationData } = await updateBeneficiaries({
        variables: {
          input: {
            personId,
            applicationType,
            effectiveStartDate: values.effectiveStartDate,
            primaryBeneficiaries: values.primaryBeneficiaries.map((b) => toMutationInput(b, contactInfoEnabled)),
            contingentBeneficiaries: values.contingentBeneficiaries.map((b) => toMutationInput(b, contactInfoEnabled))
          }
        }
      });

      const response = mutationData?.updateBeneficiaries;
      if (response?.success) {
        showSnackbar("Beneficiaries updated");
        goToBeneficiariesList();
      } else {
        setBannerError(response?.message ?? "Could not update beneficiaries.");
      }
    } catch (mutationError) {
      setBannerError(mutationError instanceof ApolloError ? mutationError.message : "Could not update beneficiaries.");
    }
  }

  const cancelButton = (
    <div className="flex cursor-pointer items-center" onClick={handleCancelClick} data-testid="cancel-button">
      <IconButton name="arrow_back" iconClassName="text-sys-brand-on-surface" />
      <span className="text-title-large">Cancel</span>
    </div>
  );

  if (error) {
    return (
      <FocusedPageLayout cancelButton={cancelButton}>
        <ErrorBoundary variant="inline" title="Failed to Load Beneficiaries" message={error.message} />
      </FocusedPageLayout>
    );
  }

  if (!loading && !personId) {
    return (
      <FocusedPageLayout cancelButton={cancelButton}>
        <ErrorBoundary
          variant="inline"
          title="Person Not Found"
          message="The person you're looking for could not be found. They may not exist or you may not have permission to view their profile."
        />
      </FocusedPageLayout>
    );
  }

  if (
    !loading &&
    personId &&
    isBeneficiaryApplicationType(applicationType) &&
    !allowedApplicationTypes.includes(applicationType)
  ) {
    return <Navigate to="/people/:pgaId/benefits/beneficiaries" params={{ pgaId }} replace />;
  }

  return (
    <FocusedPageLayout
      cancelButton={cancelButton}
      stickyActions
      clusterActions={[
        {
          text: "Clear Data",
          variant: "outlined",
          onClick: () => {
            if (loading || saving) return;
            formActionsRef.current?.clearData();
          },
          disabled: loading || saving,
          testId: "clear-beneficiaries-data"
        },
        {
          text: "Save",
          onClick: () => {
            if (loading || saving || !formState.canSave || !formState.isDirty) return;
            formActionsRef.current?.submit();
          },
          disabled: loading || saving || !formState.canSave || !formState.isDirty,
          loading: saving,
          testId: "save-beneficiaries"
        }
      ]}
    >
      <PersonObjectHeading pgaId={pgaId} hideActions disableScrollAdjustment />
      {bannerError ? (
        <Banner
          inline
          variant="error"
          description="The beneficiaries could not be updated."
          subText={bannerError}
          className="mt-6"
        />
      ) : null}
      {!loading && personId ? (
        <div>
          <UpdateBeneficiariesForm
            key={formResetKey}
            defaultValues={defaultValues}
            applicationType={applicationType}
            onSubmit={onSubmit}
            formActionsRef={formActionsRef}
            disabled={saving}
            contactInfoEnabled={contactInfoEnabled}
            onFormStateChange={setFormState}
          />
          <DiscardBeneficiariesChangesDialog
            open={showDiscardDialog}
            onDismiss={() => setShowDiscardDialog(false)}
            onDiscard={() => {
              setShowDiscardDialog(false);
              handleCancel();
            }}
          />
        </div>
      ) : (
        <UpdateBeneficiariesFormSkeleton />
      )}
    </FocusedPageLayout>
  );
}
